import { useState, useEffect, useMemo, useRef } from "react";
import classnames from 'classnames';
import './FeaturedPage.scss';
import { DashboardContent } from "@/components/dashboard";
import { useUser } from "@/contexts/UserContext";
import { ByzzerButton, ByzzerSelectOption } from "@byzzer/ui-components";
import { ByzzerCard } from "@/components/ByzzerCard";
import useReports from "@/hooks/useReports";
import FavoriteIcon from "@/components/icons/FavoriteIcon";
import { useTenantApi } from "@/hooks";
import { TipIcon } from "@/components/icons";
import { snakeCaseToTitleCase } from "@/utils";
import useAlerts from "@/hooks/useAlerts";
import { ByzzerModal, alert as byzzerAlert } from "@/components/form";
import ReportInformation from "@/views/ReportInformation";
import { reportDescriptionUrl } from "@/config/globalVars";
import useCanRunReport from "@/hooks/useCanRunReport";
import { FeaturedProduct, FeaturedSection } from "@/types/ApiTypes";
import { NavigationSourceEnum } from "@/types/AnalyticsTypes";
import useStories from "@/hooks/useStories";
import useCanCreateStory from "@/hooks/useCanCreateStory";

export type FeaturedPageProps = {}

const baseClassName = 'byz-featured-page';

const FEATURED_PAGE_NAVIGATION_SOURCE = NavigationSourceEnum.FEATURED_PAGE;

export const FeaturedPage = ({
    ...props
}: FeaturedPageProps) => {
    const userReports = useReports({onlySubscribed: true});
    const userAlerts = useAlerts();
    const userStories = useStories();
    const {subscribedSkus, user, featuredAndPopularProducts = []} = useUser();
    const [featuredGroups, setFeaturedGroups] = useState<ByzzerSelectOption<any, FeaturedSection>[]>([]);
    const [showInformationView, setShowInformationView] = useState(false);
    const [selectedProduct, setSelectedProduct] = useState<typeof userProducts[number]>();
    const [selectedFeatureProduct, setSelectedFeatureProduct] = useState<typeof featuredAndPopularProducts[number]>();
    const [favoriteSkus, setFavoriteSkus] = useState<string[]>([]);
    const [canRunReports, setCanRunReports] = useState(false);
    
    const {createUserFavorite, deleteUserFavorite, getUserFavorite} = useTenantApi();
    const abortControllerRef = useRef<AbortController>(new AbortController());

    const validateReportCountAndShow = useCanRunReport({baseClassName})
    const validateStoryCountAndShow = useCanCreateStory();

    useEffect(() => {
        if (featuredAndPopularProducts) {

            const uniqueSections = featuredAndPopularProducts
                .reduce((acc: typeof featuredGroups, featuredProduct) => {
                    if (acc.find((existingFeaturedProduct) => existingFeaturedProduct.value === featuredProduct.section)) {
                        return acc;
                    }
                    return [
                        ...acc, 
                        {
                            display: featuredProduct.section,
                            value: featuredProduct.section,
                        }
                    ];
                }, [] as typeof featuredGroups)
                .toSorted((a, b) => b.display.localeCompare(a.display));
                
            setFeaturedGroups(uniqueSections);
        }
    }, [featuredAndPopularProducts]);

    const featuredSkus = featuredAndPopularProducts.map(({sku}) => sku);

    const userProducts = useMemo(() => {
        return [ // todo: add other products - stories, alerts, simulators when requested
            ...userReports,
            ...userStories
            // ...userAlerts,
            // ...userSimulators, 
        ].filter((userReport) => featuredSkus.includes(userReport.sku))
    }, [userReports]);

    async function getFavorites() {
        const skus = await getUserFavorite('subscription', abortControllerRef.current?.signal);
        setFavoriteSkus(skus);
    }

    function handleCardClick(
        product: (typeof userProducts)[number], 
        featuredProduct: (typeof featuredAndPopularProducts)[number]
    ) {
        if (product.type === 'report') {
            if (subscribedSkus.includes(product.sku)) {
                validateReportCountAndShow(product, {
                    navigationSource: FEATURED_PAGE_NAVIGATION_SOURCE,
                    supportingData: featuredProduct,
                });
            } else {
                byzzerAlert({
                    content: <p>Please reach out to your Byzzer sales representative</p>,
                });
            }
        } else if (product.type === 'story') {
            validateStoryCountAndShow(product, {
                navigationSource: FEATURED_PAGE_NAVIGATION_SOURCE,
                supportingData: featuredProduct,
            });
            // tbd - however alerts are handled in AlertList when clicked
        } // else if (product.type -> Stories, Simulators, etc_)
    }

    useEffect(() => {
        if (user?.role) {
            setCanRunReports(['user', 'admin'].includes(user?.role));
        }
    }, [user]);

    useEffect(() => {
        getFavorites();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    function handleReportClick(
        product: (typeof userProducts)[number],
        featuredProduct: (typeof featuredAndPopularProducts)[number]
    ) {
        const { reportType, sku, type } = product;
        if (subscribedSkus.includes(sku)) {
            if (type === 'story') {
                validateStoryCountAndShow(product);
            } else if (type === 'report') {
                validateReportCountAndShow(
                    {reportType, sku},
                    {
                        navigationSource: FEATURED_PAGE_NAVIGATION_SOURCE,
                        supportingData: featuredProduct
                    }
                );
            }
        } else {
            byzzerAlert({
                content: (
                    <p>Please reach out to your Byzzer sales representative</p>
                ),
            });
        }
    }

    async function handleFavoriteClick(productSku: string) { // todo: combine with the same function from SubscriptionReports.  
        /** Delete If Available */
        if (favoriteSkus.includes(productSku)) {
            favoriteSkus.splice(favoriteSkus.indexOf(productSku), 1);
            // expanding the array before setting is necessary to change the reference and force react to update
            setFavoriteSkus([...favoriteSkus]);
            // we only need to this check when removing a favorite because it wouldn't be visible to toggle otherwise

            await deleteUserFavorite('subscription', productSku);
        } else {
            /** Add If not available */
            favoriteSkus.push(productSku);
            setFavoriteSkus([...favoriteSkus]);
            await createUserFavorite('subscription', productSku);
        }
    }

    function handleTipClick(
        showInformationModal: typeof showInformationView,
        product: (typeof userProducts)[number], 
        featuredProduct: (typeof featuredAndPopularProducts)[number]
    ) {
        setShowInformationView(true);
        setSelectedProduct(product);
        setSelectedFeatureProduct(featuredProduct);
    }

    return (
        <DashboardContent 
            title={'Featured'} 
            links={[]} 
            className={classnames(`${baseClassName}__dashboard-content`)}
        >
            <div className={classnames(`${baseClassName}__groups`)}>
                {featuredGroups.map((featuredGroup, index) => {
                    return (
                        <div className={classnames(`${baseClassName}__group`)} key={featuredGroup.value}>
                            <div className={classnames(`${baseClassName}__group-title`)}>
                                {featuredGroup.display}
                            </div>
                            <div className={classnames(`${baseClassName}__cards`)}>
                                {featuredAndPopularProducts
                                    .filter((featuredProduct) => featuredProduct.section === featuredGroup.value)
                                    .map((featuredProduct) => {
                                        const productInfo = userProducts.find((product) => product.sku === featuredProduct.sku)!;

                                        if (!productInfo) {
                                            return <></>;
                                        }
                                        const reportTypeBadge = featuredGroup.display?.trim()?.toLowerCase() === 'wellness reports' ? 'Wellness' : snakeCaseToTitleCase(productInfo?.reportType);
                                        const sku = productInfo.sku;
                                        const showBadges = featuredProduct.type !== 'most_popular'; // per ticket BYZ-13912, not showing badges for most popular products
                                        const showFavoriteIcon = productInfo.type === 'report';
                                        const showInformationTip = productInfo.type !== 'story';
                                        return (
                                            <ByzzerCard
                                                key={featuredProduct.id}
                                                onClick={() => handleCardClick(productInfo, featuredProduct)}
                                                shouldTrackClick={true}
                                                trackClickName={`featured_product_card_clicked`}
                                                trackClickData={featuredProduct}
                                                title={productInfo?.title}
                                                description={productInfo.description}
                                                thumbnailUrl={productInfo.thumbnailUrl}
                                                thumbnailImageSize={'lg'}
                                                isLocked={!subscribedSkus.includes(sku)}
                                                tagText={featuredProduct.tag}
                                                tagColor={featuredProduct.tagColor}
                                                subTitle={featuredProduct?.subtitle}
                                                actions={
                                                    <> 
                                                        {showFavoriteIcon && (
                                                            // @ts-expect-error: FavoriteIcon doesnt have types yet, is jsx.
                                                            <FavoriteIcon
                                                                trackClick={`Favorited Report clicked`}
                                                                selected={favoriteSkus.includes(sku)}
                                                                onClick={() => handleFavoriteClick(sku)}
                                                            />
                                                        )}
                                                        {showInformationTip && (
                                                            // @ts-expect-error: TipIcon doesnt have types yet, is jsx.
                                                            <TipIcon
                                                                trackClick={`Report Info (${productInfo.title}) clicked`}
                                                                onClick={() => handleTipClick(showInformationTip, productInfo, featuredProduct)}
                                                            />
                                                        )}
                                                    </>
                                                }
                                                badges={showBadges ? [reportTypeBadge] : []}
                                            />
                                        );
                                    })
                                }
                            </div>
                        </div>
                    )
                })}
                {selectedProduct?.title && (
                    <ByzzerModal
                        show={showInformationView}
                        onClose={() => setShowInformationView(!showInformationView)}
                        heading={selectedProduct?.title}
                        size={'large'}
                        headerType={'normal'}
                        extraIcon={
                            canRunReports &&
                            subscribedSkus.includes(selectedProduct.sku) && (
                                <ByzzerButton label={'Run Report'} onClick={() => handleReportClick(selectedProduct, selectedFeatureProduct!)}/>
                            )
                        }
                    >
                        <ReportInformation
                            sku={selectedProduct.sku}
                            disabled={!canRunReports || !subscribedSkus.includes(selectedProduct.sku)}
                            url={
                                selectedProduct?.metadata?.detailsUrl ||
                                `${reportDescriptionUrl}/${selectedProduct?.sku}.html`
                            }
                            runReport={() => handleReportClick(selectedProduct, selectedFeatureProduct!)}
                        />
                    </ByzzerModal>
                )}
            </div>
        </DashboardContent>
    );

};

export default FeaturedPage;

FeaturedPage.displayName = 'FeaturedPage';